import { Box, Grid } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { IPersonalIdentificationFields } from 'features/files/parties/interfaces/PersonalInformation';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { useContext, useEffect, useMemo } from 'react';
import { AgentNetDropdownSelector, AgentNetTextInput } from 'ui-kit/inputs';
import PersonalIdForm from '../../files/file-create/PersonalIdForm';
import { DrawerFileSectionProps } from './interfaces/PartyDrawer';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    marginBottom: {
      marginBottom: theme.spacing(1),
    },
  }),
);

export const underWriterOptions = [
  { name: 'First American Title Insurance Company', value: 'FATIC' },
  {
    name: 'First American Title Insurance Company of Louisiana',
    value: 'FATIC-LA',
  },
  { name: 'First American Title Guaranty Company', value: 'FATGC' },
];

const DrawerFileSection = ({
  value,
  onChange,
  showAllValidation,
  validationErrors,
  showPersonalIdForm = true,
  setIsPersonalIdentificationFormGroupValid,
  qaAttrPrefix = 'new-verification',
  underwriterOptions,
  isLoadingUnderwriters = false,
}: DrawerFileSectionProps) => {
  const classNames = useStyles();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm, profile } = profileCtx;

  const profileUnderwriterOptions = useMemo(() => {
    if (!profile?.firms || !userFirm?.firmId) {
      return underWriterOptions;
    }

    const selectedFirm = profile.firms.find((firm) => String(firm.firmId) === String(userFirm.firmId));

    if (!selectedFirm?.offices) {
      return underWriterOptions;
    }

    const underwritersMap = new Map<string, string>();
    selectedFirm.offices.forEach((office) => {
      office.accounts?.forEach((account) => {
        if (account.underwriterCode && account.underwriterName) {
          underwritersMap.set(account.underwriterCode, account.underwriterName);
        }
      });
    });
    const options = Array.from(underwritersMap.entries()).map(([value, name]) => ({
      value,
      name,
    }));

    return options.length > 0 ? options : underWriterOptions;
  }, [profile?.firms, userFirm?.firmId]);

  const options = underwriterOptions ?? profileUnderwriterOptions;

  // Only the firm underwriter list auto-selects; the profile-derived list keeps its existing behavior.
  useEffect(() => {
    if (!underwriterOptions || underwriterOptions.length !== 1 || value?.underwriterCode) {
      return;
    }

    onChange({ ...value, underwriterCode: underwriterOptions[0].value });
  }, [underwriterOptions]);

  const handleUnderwriterChange = (underwriterCode: string) => {
    onChange({
      ...value,
      underwriterCode,
    });
  };

  const handleFileNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...value,
      fileNumber: event.target.value,
    });
  };

  const handlePersonalInfoChange = (personalInformation: IPersonalIdentificationFields) => {
    onChange({
      ...value,
      personalInformation,
    });
  };

  const hasUnderwriterError = showAllValidation && validationErrors?.some((err) => err.field === 'underwriterCode');

  return (
    <Box width={'100%'}>
      <Grid container spacing={3} className={classNames.marginBottom}>
        <Grid item sm={6}>
          <AgentNetDropdownSelector
            name="underwriterCode"
            label="Underwriter"
            required
            options={options}
            value={value?.underwriterCode || ''}
            id="underwriterCode"
            menuOption={handleUnderwriterChange}
            disabled={isLoadingUnderwriters}
            dropdowntype="outlined"
            data-testid={`${qaAttrPrefix}-listbox-underwriter`}
            showValidation={showAllValidation}
            errs={validationErrors}
            error={hasUnderwriterError}
          />
        </Grid>
        <Grid item sm={6}>
          <AgentNetTextInput
            fullWidth
            required
            variant="outlined"
            label="File Number"
            id="fileNumber"
            name="fileNumber"
            showValidation={showAllValidation}
            data-testid={`${qaAttrPrefix}-input-filenumber`}
            value={value?.fileNumber || ''}
            onChange={handleFileNumberChange}
            errs={validationErrors}
          />
        </Grid>
      </Grid>

      {showPersonalIdForm && setIsPersonalIdentificationFormGroupValid && (
        <PersonalIdForm
          setIsValid={setIsPersonalIdentificationFormGroupValid}
          showAllValidation={showAllValidation}
          value={value?.personalInformation || {}}
          onChange={handlePersonalInfoChange}
          isCreateFile
          showOnlyFirstAndLastName
          isPartyDrawer
          qaAttrPrefix={`${qaAttrPrefix}-input-`}
        />
      )}
    </Box>
  );
};

export default DrawerFileSection;
